import React from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import LabelField from "../Form/FormFields/LabelField";
import DynamicLoader from "../Form/FormFields/DynamicLoader";
import Accordion from "../ui/Accordion";
import NoteComponent from "../ui/table/Notes";

interface AreaChartData {
  name: string;
  value: number;
}

interface DynamicAreaChartProps {
  chartListData: any;
  width?: string;
}

const DynamicAreaChart: React.FC<DynamicAreaChartProps> = ({
  chartListData,
  width,
}) => {
  const selectorData = useSelector((state: any) => state?.dynamicData);
  const notes = chartListData?.note;
  const noteData = chartListData?.remarks;
  const isLoading = selectorData.loaders[`report/chart/${chartListData?.id}`];

  const areachartData: AreaChartData[] = selectorData[chartListData?.id] || [];

  const processedData = areachartData.map((item) => ({
    name: item.name,
    value: Number(item.value),
  }));

  const maxValue = Math.max(...processedData.map((item) => item.value), 100);

  const chartData = processedData.map((item) => ({
    name: item.name,
    value: (item.value / maxValue) * 100,
  }));

  const series = [
    {
      name: "Pending",
      data: chartData.map((item) => item.value),
    },
  ];

  const chartOptions: any = {
    chart: {
      type: "area",
      zoom: { enabled: false },
      toolbar: {
        show: true,
        tools: {
          download: false,
        },
      },
      animations: {
        enabled: true,
        easing: "easeInOutBounce",
        speed: 1200,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 700,
        },
      },
      dropShadow: {
        enabled: true,
        top: 10,
        left: 0,
        blur: 6,
        opacity: 0.25,
        color: "#1e40af", // deep blue shadow
      },
    },
    xaxis: {
      categories: chartData.map((item) => item.name),
      labels: {
        rotate: -45,
        style: { fontSize: "12px" },
      },
      title: {
        text: "Role",
        style: { fontWeight: 600 },
      },
    },
    yaxis: {
      max: 100,
      labels: {
        formatter: (val: number) => `${val.toFixed(0)}%`,
      },
      title: {
        text: "Pending %",
      },
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: (val: number) => `${val.toFixed(2)}%`,
      },
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "dark",
        type: "vertical",
        shadeIntensity: 0.6,
        gradientToColors: ["#6a11cb"], // End color
        inverseColors: false,
        opacityFrom: 0.9,
        opacityTo: 0.7,
        stops: [0, 100],
      },
    },
    colors: ["#2575fc"], // Start color
    markers: {
      size: 5,
      colors: ["#ffffff"],
      strokeColor: "#2575fc",
      strokeWidth: 3,
      hover: {
        size: 7,
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: { height: 300 },
        },
      },
    ],
  };

  return (
    <div className={`grid gap-2 ${width && width}`}>
      <div className="bg-white p-2 rounded-lg w-full">
        <h2 className="text-lg font-semibold mb-2 flex items-center">
          <LabelField label={chartListData?.title} />
          {notes && (
            <NoteComponent notes={noteData} modalTitle={chartListData?.title} />
          )}
        </h2>
        {isLoading ? (
          <DynamicLoader loading={isLoading} />
        ) : (
          <div className="w-full" style={{ height: 320 }}>
            <ReactApexChart
              options={chartOptions}
              series={series}
              type="area"
              height="100%"
            />

            <Accordion
              title="AI Insights"
              componentID={chartListData?.id}
              chartData={chartListData}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicAreaChart;
